import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaSave, FaSpinner, FaUser, FaLock, FaBell, FaCalendarAlt, FaStar, FaCheck, FaExchangeAlt, FaEnvelope, FaPhone, FaKey, FaTrashAlt, FaInfoCircle } from 'react-icons/fa';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import './account-settings.css';
import { useAuthContext } from '../../context/AuthContext';
import { authApi } from '../../api/auth';
import { eventApi } from '../../api/events/eventApi';
import { STORAGE_KEYS } from '../../api/config';
import apiClient from '../../api/index';

const Account_setting = () => {
  const { user, updateUser, isAuthenticated, deleteAccountRequest, deleteAccountVerify, currentEvent, switchEvent, setDefaultEvent: contextSetDefaultEvent, events: authEvents } = useAuthContext();

  // Loading states
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Active tab state
  const [activeTab, setActiveTab] = useState('profile');

  // User profile state
  const [profile, setProfile] = useState({
    id: '',
    user_name: '',
    email: '',
    mobile: '',
    role: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  // Notification settings state
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    sms_notifications: false,
    new_card_alerts: true,
    payment_alerts: true,
    marketing_emails: false
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [defaultEvent, setDefaultEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showChangeDefaultModal, setShowChangeDefaultModal] = useState(false);
  const [eventToChange, setEventToChange] = useState(null);

  // Alert states
  const [showProfileAlert, setShowProfileAlert] = useState(false);
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  const [showNotificationAlert, setShowNotificationAlert] = useState(false);
  const [showEventAlert, setShowEventAlert] = useState(false);
  const [error, setError] = useState('');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch user data and events on component mount
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserProfile();
      loadEvents();
      loadDefaultEvent();
    }
  }, [isAuthenticated, user]);

  // Fetch user profile from API
  const fetchUserProfile = async () => {
    setFetching(true);
    try {
      const userId = user?.id;
      if (!userId) {
        console.warn('No user ID available');
        setFetching(false);
        return;
      }

      const response = await apiClient.get(`/user/profile/${userId}`);
      const userData = response?.data?.data || response?.data || response;

      setProfile({
        id: userData.id || userId,
        user_name: userData.user_name || userData.username || '',
        email: userData.email || '',
        mobile: userData.mobile || userData.phone || '',
        role: getRoleName(userData.role)
      });

      const savedNotifications = localStorage.getItem('notification_preferences');
      if (savedNotifications) {
        setNotifications(JSON.parse(savedNotifications));
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      toast.error('Failed to load user profile');
      // Fallback to user context data
      setProfile({
        id: user?.id,
        user_name: user?.user_name || user?.username || '',
        email: user?.email || '',
        mobile: user?.mobile || user?.phone || '',
        role: getRoleName(user?.role)
      });
    } finally {
      setFetching(false);
    }
  };

  // Update user profile via API
  const updateUserProfile = async (updateData) => {
    try {
      const userId = profile.id || user?.id;
      if (!userId) {
        throw new Error('No user ID available');
      }

      const response = await apiClient.post(`/user/profile-update/${userId}`, updateData);
      return response;
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err;
    }
  };

  const loadEvents = async () => {
    try {
      const userEvents = user?.events || [];

      if (userEvents.length > 0) {
        setEvents(userEvents);
        const savedEventId = localStorage.getItem(STORAGE_KEYS.EVENT_ID);
        if (savedEventId) {
          const currentEvent = userEvents.find(e => e.id === parseInt(savedEventId));
          if (currentEvent) {
            setSelectedEvent(currentEvent);
          } else {
            setSelectedEvent(userEvents[0]);
          }
        } else if (userEvents.length > 0) {
          setSelectedEvent(userEvents[0]);
        }
      }
    } catch (err) {
      console.error('Error loading events:', err);
      toast.error('Failed to load events');
    }
  };

  const loadDefaultEvent = () => {
    const savedDefaultEventId = localStorage.getItem('defaultEventId');
    const savedDefaultEvent = localStorage.getItem('defaultEvent');

    if (savedDefaultEventId && savedDefaultEvent) {
      try {
        const defaultEventData = JSON.parse(savedDefaultEvent);
        setDefaultEvent(defaultEventData);
      } catch (err) {
        console.error('Error parsing default event:', err);
        if (events.length > 0) {
          setDefaultEvent(events[0]);
          localStorage.setItem('defaultEventId', events[0].id.toString());
          localStorage.setItem('defaultEvent', JSON.stringify(events[0]));
        }
      }
    } else if (events.length > 0) {
      setDefaultEvent(events[0]);
      localStorage.setItem('defaultEventId', events[0].id.toString());
      localStorage.setItem('defaultEvent', JSON.stringify(events[0]));
    }
  };

  const getRoleName = (roleId) => {
    const roles = {
      0: 'User',
      1: 'Administrator',
      2: 'Service Provider',
      3: 'Agent'
    };
    return roles[roleId] || 'User';
  };

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // Handle notification toggle changes
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotifications(prev => ({ ...prev, [name]: checked }));
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        email: profile.email,
        mobile: profile.mobile
      };

      const res = await updateUserProfile(updateData);

      // Update local user context
      if (updateUser) {
        updateUser({ ...user, email: profile.email, mobile: profile.mobile });
      }

      setShowProfileAlert(true);
      handleApiResponse(res);
      setTimeout(() => setShowProfileAlert(false), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
      handleApiResponse(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters long');
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setError("New passwords don't match");
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.changePassword({
        oldpassword: passwordData.current_password,
        newpassword: passwordData.new_password
      });

      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });

      setShowPasswordAlert(true);
      handleApiResponse(response);
      setTimeout(() => setShowPasswordAlert(false), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
      handleApiResponse(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification settings update
  const handleNotificationUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      localStorage.setItem('notification_preferences', JSON.stringify(notifications));
      setShowNotificationAlert(true);
      toast.success('Notification preferences saved!');
      setTimeout(() => setShowNotificationAlert(false), 3000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      toast.error('Failed to save notification preferences');
    } finally {
      setLoading(false);
    }
  };

  // Set as default event
  const handleSetDefaultEvent = async (event) => {
    setLoading(true);
    try {
      if (contextSetDefaultEvent) {
        await contextSetDefaultEvent(event);
      } else {
        await eventApi.setDefaultEvent(event.id);
        localStorage.setItem('defaultEventId', event.id.toString());
        localStorage.setItem('defaultEvent', JSON.stringify(event));
        localStorage.setItem(STORAGE_KEYS.UID, event.event_address);
        localStorage.setItem(STORAGE_KEYS.EVENT_ID, event.id);
        localStorage.setItem('selectedEventId', event.id.toString());
        toast.success(`${event.event_title} set as default event!`);
      }
      setDefaultEvent(event);
      setSelectedEvent(event);
      setShowEventAlert(true);
      setTimeout(() => setShowEventAlert(false), 3000);
    } catch (err) {
      console.error('Error setting default event:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to set default event');
    } finally {
      setLoading(false);
    }
  };

  // Change current active event
  const handleChangeCurrentEvent = (event) => {
    setEventToChange(event);
    setShowChangeDefaultModal(true);
  };

  const confirmChangeEvent = () => {
    if (eventToChange) {
      if (switchEvent) {
        switchEvent(eventToChange);
      } else {
        localStorage.setItem(STORAGE_KEYS.UID, eventToChange.event_address);
        localStorage.setItem(STORAGE_KEYS.EVENT_ID, eventToChange.id);
        localStorage.setItem('selectedEventId', eventToChange.id.toString());
        setSelectedEvent(eventToChange);
        setShowChangeDefaultModal(false);
        toast.success(`Switched to ${eventToChange.event_title}`);
        setTimeout(() => window.location.reload(), 1000);
      }
    }
  };

  const handleDeleteRequest = async () => {
    setDeleteLoading(true);
    try {
      await deleteAccountRequest({ mobile: profile.mobile });
      setDeleteStep(2);
      toast.info('Verification code sent to your mobile number');
    } catch (err) {
      console.error('Delete request error:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteVerify = async () => {
    if (!deleteOtp) {
      toast.error('Please enter the verification code');
      return;
    }
    setDeleteLoading(true);
    try {
      await deleteAccountVerify({
        mobile: profile.mobile,
        otp: deleteOtp
      });
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Delete verify error:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('sw-TZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (fetching) {
    return (
      <Container className="settings-container p-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <FaSpinner className="fa-spin" size={48} color="#4169e1" />
            <p className="mt-3">Loading account settings...</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="settings-container p-4">
      <h1 className="stylish-heading text-center mb-4" style={{ color: '#4169e1' }}>Account Settings</h1>

      {/* Event Management Banner */}
      <Card className="mb-4 event-banner-card">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <div className="event-icon-wrapper">
                  <FaCalendarAlt size={28} />
                </div>
                <div className="ms-3">
                  <h6 className="mb-1 text-muted">Current Active Event</h6>
                  {selectedEvent ? (
                    <>
                      <h4 className="mb-1 event-title">{selectedEvent.event_title}</h4>
                      <div className="event-details">
                        <span className="badge bg-light text-dark me-2">{selectedEvent.event_type || 'Event'}</span>
                        <small className="text-muted">📅 {formatDate(selectedEvent.event_date)}</small>
                      </div>
                    </>
                  ) : (
                    <p className="mb-0">No event selected</p>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              {defaultEvent ? (
                <div className="d-inline-block text-md-end text-start">
                  <div className="small text-muted mb-1 fw-bold">
                    <FaStar className="text-warning me-1" /> Default Event
                  </div>
                  <div className="fw-bold text-dark fs-6 d-flex align-items-center justify-content-md-end justify-content-start gap-2">
                    <span>{defaultEvent.event_title}</span>
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: '12px' }}>
                      ★ Default
                    </span>
                  </div>
                </div>
              ) : null}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs
        id="account-settings-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="settings-nav-tabs mb-4"
      >
        {/* Profile Information Tab */}
        <Tab eventKey="profile" title={<span><FaUser className="me-2" /> Profile Information</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Profile Information</h3>
              <p className="text-muted mb-4">Update your personal information and contact details</p>

              {showProfileAlert && (
                <Alert variant="success" onClose={() => setShowProfileAlert(false)} dismissible className="mb-4">
                  <FaCheck className="me-2" /> Profile information updated successfully!
                </Alert>
              )}

              {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

              <Form onSubmit={handleProfileUpdate}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">
                        <FaUser className="me-2" /> Username
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="user_name"
                        value={profile.user_name}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">Username cannot be changed</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">
                        <FaEnvelope className="me-2" /> Email Address
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        placeholder="Enter your email address"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">
                        <FaPhone className="me-2" /> Phone Number
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile"
                        value={profile.mobile}
                        onChange={handleProfileChange}
                        placeholder="Enter your phone number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Role</Form.Label>
                      <Form.Control
                        type="text"
                        value={profile.role}
                        readOnly
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Your role cannot be changed. Contact system administrator for role changes.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {loading ? ' Saving...' : ' Update Profile'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* My Events Tab */}
        <Tab eventKey="events" title={<span><FaCalendarAlt className="me-2" /> My Events</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">My Events</h3>
              <p className="text-muted mb-4">View all your events and manage default event settings</p>

              {showEventAlert && (
                <Alert variant="success" onClose={() => setShowEventAlert(false)} dismissible className="mb-4">
                  <FaStar className="me-2" /> Your default event has been changed successfully!
                </Alert>
              )}

              {events.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                  <FaCalendarAlt size={48} className="mb-3 opacity-50" />
                  <Alert.Heading>No Events Found</Alert.Heading>
                  <p>You haven't created any events yet.</p>
                </Alert>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Event Title</th>
                          <th>Event Type</th>
                          <th>Event Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event, index) => (
                          <tr key={event.id} className={defaultEvent?.id === event.id ? 'table-warning' : ''}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{event.event_title}</strong>
                              {defaultEvent?.id === event.id && (
                                <span className="badge bg-warning ms-2">Default</span>
                              )}
                            </td>
                            <td>{event.event_type || 'N/A'}</td>
                            <td>{formatDate(event.event_date)}</td>
                            <td>
                              <span className={`badge ${event.status === 1 ? 'bg-success' : 'bg-warning'}`}>
                                {event.status === 1 ? 'Active' : 'Pending'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {defaultEvent?.id !== event.id && (
                                  <Button variant="outline-primary" size="sm" onClick={() => handleSetDefaultEvent(event)}>
                                    <FaStar className="me-1" /> Set Default
                                  </Button>
                                )}
                                <Button variant="outline-secondary" size="sm" onClick={() => handleChangeCurrentEvent(event)}>
                                  <FaExchangeAlt className="me-1" /> Switch
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="alert alert-info mt-3">
                    <FaStar className="text-warning me-2" />
                    <strong>Default Event:</strong> {defaultEvent?.event_title || 'Not set'} — This event is automatically selected when you log in.
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Security Tab - Change Password */}
        <Tab eventKey="security" title={<span><FaLock className="me-2" /> Security</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Change Password</h3>
              <p className="text-muted mb-4">Update your password to maintain account security</p>

              {showPasswordAlert && (
                <Alert variant="success" onClose={() => setShowPasswordAlert(false)} dismissible className="mb-4">
                  <FaCheck className="me-2" /> Password updated successfully!
                </Alert>
              )}

              {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

              <Form onSubmit={handlePasswordUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label className="settings-form-label">
                    <FaKey className="me-2" /> Current Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter your current password"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="settings-form-label">New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter new password (min. 6 characters)"
                  />
                  <Form.Text className="text-muted">Password must be at least 6 characters long</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="settings-form-label">Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="new_password_confirmation"
                    value={passwordData.new_password_confirmation}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Confirm your new password"
                  />
                </Form.Group>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaLock className="me-2" />}
                    {loading ? ' Updating...' : ' Update Password'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* Notification Settings Tab */}
        <Tab eventKey="notifications" title={<span><FaBell className="me-2" /> Notifications</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Notification Settings</h3>
              <p className="text-muted mb-4">Manage how you receive notifications and alerts</p>

              {showNotificationAlert && (
                <Alert variant="success" onClose={() => setShowNotificationAlert(false)} dismissible className="mb-4">
                  <FaCheck className="me-2" /> Notification preferences saved successfully!
                </Alert>
              )}

              <Form onSubmit={handleNotificationUpdate}>
                <div className="mb-4">
                  <h5 className="mb-3">Notification Channels</h5>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="email-notifications"
                      label="Email Notifications"
                      name="email_notifications"
                      checked={notifications.email_notifications}
                      onChange={handleNotificationChange}
                    />
                    <Form.Text className="text-muted">Receive notifications via email</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="sms-notifications"
                      label="SMS Notifications"
                      name="sms_notifications"
                      checked={notifications.sms_notifications}
                      onChange={handleNotificationChange}
                    />
                    <Form.Text className="text-muted">Receive notifications via SMS (standard messaging rates may apply)</Form.Text>
                  </Form.Group>
                </div>

                <div className="mb-4">
                  <h5 className="mb-3">Notification Types</h5>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="new-card-alerts"
                      label="New Card Alerts"
                      name="new_card_alerts"
                      checked={notifications.new_card_alerts}
                      onChange={handleNotificationChange}
                    />
                    <Form.Text className="text-muted">Get notified when new cards are created</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="payment-alerts"
                      label="Payment Alerts"
                      name="payment_alerts"
                      checked={notifications.payment_alerts}
                      onChange={handleNotificationChange}
                    />
                    <Form.Text className="text-muted">Get notified when payments are received</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="marketing-emails"
                      label="Marketing Emails"
                      name="marketing_emails"
                      checked={notifications.marketing_emails}
                      onChange={handleNotificationChange}
                    />
                    <Form.Text className="text-muted">Receive updates about new features and promotions</Form.Text>
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {loading ? ' Saving...' : ' Save Preferences'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* Delete Account Tab */}
        <Tab eventKey="delete-account" title={<span className="text-danger"><FaTrashAlt className="me-2" /> Delete Account</span>}>
          <Card className="settings-card border-danger">
            <Card.Body>
              <h3 className="settings-title text-danger">Delete Account</h3>
              <p className="text-muted mb-4">Permanently remove your account and all associated data</p>

              <div className="alert alert-warning mb-4">
                <div className="d-flex">
                  <FaInfoCircle size={24} className="me-3 text-warning" />
                  <div>
                    <strong>Warning: This action cannot be undone!</strong>
                    <p className="mb-0 small mt-1">
                      Deleting your account will permanently remove your personal information, all events, guest lists,
                      pledges, contributions, and payment history associated with your account.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border rounded p-4 bg-light">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h6 className="mb-1">Ready to delete your account?</h6>
                    <p className="text-muted mb-0 small">
                      A verification code will be sent to your mobile number to confirm this action.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => { setDeleteStep(1); setShowDeleteModal(true); }}
                    className="px-4"
                  >
                    <FaTrashAlt className="me-2" />
                    Delete My Account
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>



      {/* Change Current Event Confirmation Modal */}
      <div className="modal show-modal" style={{ display: showChangeDefaultModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title"><FaExchangeAlt className="me-2" style={{ color: '#4a85ff' }} /> Switch Event</h5>
              <button type="button" className="btn-close" onClick={() => setShowChangeDefaultModal(false)}></button>
            </div>
            <div className="modal-body">
              {eventToChange && (
                <div>
                  <p>Are you sure you want to switch to this event?</p>
                  <div className="bg-light p-3 rounded">
                    <div className="d-flex align-items-center mb-2">
                      <FaCalendarAlt className="me-2" style={{ color: '#4a85ff' }} />
                      <strong>{eventToChange.event_title}</strong>
                    </div>
                    <div className="ms-4 ps-2">
                      <small className="text-muted d-block">Type: {eventToChange.event_type || 'N/A'}</small>
                      <small className="text-muted d-block">Date: {formatDate(eventToChange.event_date)}</small>
                    </div>
                  </div>
                  <div className="alert alert-warning mt-3 mb-0">
                    <FaExchangeAlt className="me-2" />
                    Switching will reload the page and all data will reflect the selected event.
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowChangeDefaultModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={confirmChangeEvent}><FaExchangeAlt className="me-2" /> Switch Event</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Account Deletion Modal */}
      <div className="modal show-modal" style={{ display: showDeleteModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title text-danger"><FaTrashAlt className="me-2" /> Delete Account</h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              {deleteStep === 1 ? (
                <div className="text-center p-3">
                  <FaTrashAlt size={64} className="text-danger opacity-75 mb-3" />
                  <h4>Are you absolutely sure?</h4>
                  <p className="text-muted">This action will permanently delete your account, your profile, and all associated data. This cannot be undone.</p>
                  <div className="alert alert-danger mt-3">
                    <small>We will send a verification code to <strong>{profile.mobile}</strong> to confirm this action.</small>
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <h5 className="mb-3">Enter Verification Code</h5>
                  <p className="text-muted small">We've sent a 6-digit code to <strong>{profile.mobile}</strong>. Please enter it below to confirm account deletion.</p>
                  <Form.Control
                    type="text"
                    placeholder="Enter OTP"
                    value={deleteOtp}
                    onChange={(e) => setDeleteOtp(e.target.value)}
                    className="text-center form-control-lg fw-bold"
                    maxLength={6}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>Cancel</Button>
              {deleteStep === 1 ? (
                <Button variant="danger" onClick={handleDeleteRequest} disabled={deleteLoading}>
                  {deleteLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Send Verification Code
                </Button>
              ) : (
                <Button variant="danger" onClick={handleDeleteVerify} disabled={deleteLoading}>
                  {deleteLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Confirm & Delete Account
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Account_setting;